import React, { useState, useEffect } from "react";
import api from "./Api";
import "./AddBillboard.css";

const BillboardDashboard = () => {
  const [billboards, setBillboards] = useState([]);
  const [filteredBillboards, setFilteredBillboards] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingBillboard, setEditingBillboard] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Form state
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    subDescription: "",
    size: "",
    type: "Static",
    highway: "",
    addressId: "",
    startBooking: "",
    endBooking: "",
    isAvailable: true,
    numberOfFaces: 1,
    locationURL: "",
    image: null,
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchBillboards();
    fetchAddresses();
  }, []);

  // Filter billboards when search term changes
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredBillboards(billboards);
    } else {
      const filtered = billboards.filter(
        (billboard) =>
          billboard.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          billboard.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          billboard.subDescription
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          billboard.size?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          billboard.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          billboard.highway?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBillboards(filtered);
    }
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, billboards]);

  const fetchBillboards = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/admin/billboards");
      // Handle different response structures
      const data = response.data.data || response.data;
      const billboardsArray = Array.isArray(data) ? data : [];
      setBillboards(billboardsArray);
      setFilteredBillboards(billboardsArray);
    } catch (error) {
      console.error("Error fetching billboards:", error);
      setErrorMessage("Failed to load billboards");
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await api.get("/api/admin/address/GetAll");

      let addressesData = [];
      if (Array.isArray(response.data)) {
        addressesData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        addressesData = response.data.data;
      } else if (
        response.data?.$values &&
        Array.isArray(response.data.$values)
      ) {
        addressesData = response.data.$values;
      }

      setAddresses(addressesData);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setErrorMessage("Failed to load addresses");
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };


  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
      return;
    }

    if (name === "isAvailable") {
      setFormData((prev) => ({ ...prev, [name]: value === "true" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const submitData = new FormData();

      // Append data with correct property names matching the DTO
      submitData.append("Code", formData.code);
      submitData.append("Description", formData.description);
      submitData.append("SubDescription", formData.subDescription || "");
      submitData.append("Size", formData.size);
      submitData.append("Type", formData.type);
      submitData.append("Highway", formData.highway);
      submitData.append("AddressId", formData.addressId);

      // Handle date formatting
      if (formData.startBooking) {
        submitData.append(
          "StartBooking",
          new Date(formData.startBooking).toISOString()
        );
      } else {
        submitData.append("StartBooking", new Date().toISOString());
      }

      if (formData.endBooking) {
        submitData.append(
          "EndBooking",
          new Date(formData.endBooking).toISOString()
        );
      }

      submitData.append("IsAvailable", formData.isAvailable.toString());
      submitData.append("NumberOfFaces", formData.numberOfFaces.toString());
      submitData.append("LocationURL", formData.locationURL || "");

      // Append image if exists
      if (formData.image) {
        submitData.append("Image", formData.image);
      }

      let response;
      if (editingBillboard) {
        response = await api.put(
          `/api/admin/billboards/${editingBillboard.id}`,
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        setSuccessMessage("Billboard updated successfully");
      } else {
        response = await api.post("/api/admin/billboards", submitData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSuccessMessage("Billboard added successfully");
      }

      resetForm();
      fetchBillboards();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving billboard:", error);

      if (error.response?.data) {
        setErrorMessage(`Server error: ${JSON.stringify(error.response.data)}`);
      } else {
        setErrorMessage("An error occurred while saving data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (billboard) => {
    setEditingBillboard(billboard);
    setFormData({
      code: billboard.code || "",
      description: billboard.description || "",
      subDescription: billboard.subDescription || "",
      size: billboard.size || "",
      type: billboard.type || "Static",
      highway: billboard.highway || "",
      addressId: billboard.addressId || "",
      startBooking: billboard.startBooking
        ? billboard.startBooking.split("T")[0]
        : "",
      endBooking: billboard.endBooking
        ? billboard.endBooking.split("T")[0]
        : "",
      isAvailable:
        billboard.isAvailable !== undefined ? billboard.isAvailable : true,
      numberOfFaces: billboard.numberOfFaces || 1,
      locationURL: billboard.locationURL || "",
      image: null,
    });

    if (billboard.imageUrl) {
      setImagePreview(`${billboard.imageUrl}`);
    } else {
      setImagePreview(null);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this billboard?")) {
      try {
        await api.delete(`/api/admin/billboards/${id}`);
        setSuccessMessage("Billboard deleted successfully");
        fetchBillboards();
        setTimeout(() => setSuccessMessage(""), 3000);
      } catch (error) {
        console.error("Error deleting billboard:", error);
        setErrorMessage("An error occurred while deleting");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      subDescription: "",
      size: "",
      type: "Static",
      highway: "",
      addressId: "",
      startBooking: "",
      endBooking: "",
      isAvailable: true,
      numberOfFaces: 1,
      locationURL: "",
      image: null,
    });
    setEditingBillboard(null);
    setImagePreview(null);
  };

  const getAddressName = (addressId) => {
    const address = addresses.find((a) => a.id == addressId);
    return address ? address.name : "Loading...";
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBillboards.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredBillboards.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h1>Billboard Management Dashboard</h1>
        <p>Manage all billboards in the system</p>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="message success-message">
          <span className="message-icon">✓</span>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="message error-message">
          <span className="message-icon">⚠</span>
          {errorMessage}
        </div>
      )}

      {/* Form Section */}
      <div className="form-addbillboard">
        <div className="section-header">
          <h2>{editingBillboard ? "Edit Billboard" : "Add New Billboard"}</h2>
        </div>

        <form onSubmit={handleSubmit} className="billboard-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Billboard Code *</label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                placeholder="e.g., HWY-001"
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Main description"
              />
            </div>

            <div className="form-group">
              <label>Sub Description</label>
              <textarea
                name="subDescription"
                value={formData.subDescription}
                onChange={handleChange}
                placeholder="Detailed description"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Size *</label>
              <input
                type="text"
                name="size"
                value={formData.size}
                onChange={handleChange}
                required
                placeholder="e.g., 14x48 ft"
              />
            </div>

            <div className="form-group">
              <label>Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value="Static">Static</option>
                <option value="Digital">Digital</option>
                <option value="LED">LED</option>
                <option value="Backlit">Backlit</option>
              </select>
            </div>

            <div className="form-group">
              <label>Highway *</label>
              <input
                type="text"
                name="highway"
                value={formData.highway}
                onChange={handleChange}
                required
                placeholder="Highway name"
              />
            </div>

            <div className="form-group">
              <label>Number of Faces *</label>
              <input
                type="number"
                name="numberOfFaces"
                value={formData.numberOfFaces}
                onChange={handleChange}
                required
                min="1"
                max="10"
              />
            </div>

            <div className="form-group">
              <label>Location URL</label>
              <input
                type="url"
                name="locationURL"
                value={formData.locationURL}
                onChange={handleChange}
                placeholder="Google Maps link"
              />
            </div>

            <div className="form-group">
              <label>Status *</label>
              <select
                name="isAvailable"
                value={formData.isAvailable}
                onChange={handleChange}
                required
              >
                <option value={true}>Available</option>
                <option value={false}>Booked</option>
              </select>
            </div>

            <div className="form-group">
              <label>Address *</label>
              <select
                name="addressId"
                value={formData.addressId}
                onChange={handleChange}
                required
                disabled={addresses.length === 0}
              >
                <option value="">Select an address</option>
                {addresses.map((address) => (
                  <option key={address.id} value={address.id}>
                    {address.name || `Address #${address.id}`}
                  </option>
                ))}
              </select>
              {addresses.length === 0 && (
                <span className="loading-text">Loading addresses...</span>
              )}
            </div>

            {formData.isAvailable === false && (
              <>
                <div className="form-group">
                  <label>Booking Start Date</label>
                  <input
                    type="date"
                    name="startBooking"
                    value={formData.startBooking}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Booking End Date</label>
                  <input
                    type="date"
                    name="endBooking"
                    value={formData.endBooking}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}

            <div className="form-group ">
              <label>Billboard Image</label>
              <div className="file-upload">
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleChange}
                />
                <span className="file-label">Choose Image</span>
              </div>

              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-danger" disabled={loading}>
              {loading
                ? "Saving..."
                : editingBillboard
                ? "Update"
                : "Add Billboard"}
            </button>

            {editingBillboard && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Table Section */}
      <div className="table-section">
        <div className="section-header">
          <h2>Billboards List</h2>
          {/* Search Input */}
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search billboards"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <span className="items-count">
            ({filteredBillboards.length} billboards
            {searchTerm && ` filtered from ${billboards.length} total`})
          </span>
        </div>

        {loading && billboards.length === 0 ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading billboards...</p>
          </div>
        ) : billboards.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🪧</div>
            <h3>No billboards found</h3>
            <p>Add your first billboard using the form above</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="billboards-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Size</th>
                    <th>Type</th>
                    <th>Address</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-results">
                        <div className="no-results-content">
                          <span className="no-results-icon">🔍</span>
                          <h3>No matching billboards found</h3>
                          <p>Try adjusting your search terms</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((billboard) => (
                      <tr key={billboard.id}>
                        <td className="image-cell">
                          {billboard.imageUrl ? (
                            <img
                              src={`${billboard.imageUrl}`}
                              alt={billboard.description}
                            />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </td>
                        <td className="code-cell">{billboard.code}</td>
                        <td className="description-cell">
                          <div className="description-title">
                            {billboard.description}
                          </div>
                          {billboard.subDescription && (
                            <div className="description-sub">
                              {billboard.subDescription}
                            </div>
                          )}
                        </td>
                        <td className="size-cell">{billboard.size}</td>
                        <td className="type-cell">
                          <span
                            className={`type-badge ${billboard.type?.toLowerCase()}`}
                          >
                            {billboard.type}
                          </span>
                        </td>
                        <td className="address-cell">
                          {getAddressName(billboard.addressId)}
                        </td>
                        <td className="status-cell">
                          <span
                            className={`status-badge ${
                              billboard.isAvailable ? "available" : "booked"
                            }`}
                          >
                            {billboard.isAvailable ? "Available" : "Booked"}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button
                            className="btn-action edit"
                            onClick={() => handleEdit(billboard)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-action delete"
                            onClick={() => handleDelete(billboard.id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>

                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`page-btn ${
                          currentPage === number ? "active" : ""
                        }`}
                      >
                        {number}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BillboardDashboard;
